import { useContext, useEffect } from "react";
import { ApiContext, useApi } from "../apiProvider";
import { API_Version, ApiVersionInfo, ResultObj, allFetch, getControllerApiVersion } from "../Domain";


export const useUserControlApi = () => {
    const apiManagerName = "userControlApiManager";
    const context = useContext(ApiContext);
    if (!!!context) {
        throw new Error("useUserControlApi must be used within a ApiProvider");
    }

    let { apiManagers, registerApiManager } = context;
    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newUserControlApiManager = new UserControlApiManager();
            newUserControlApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("Error initializing API Manager", e);
            });
        }
    }, [apiManagers[apiManagerName]]);

    return useApi<UserControlApiManager>(apiManagerName);
};

interface UserControlApiInterface {
    Signin: (obj: SigninParams) => Promise<ResultObj>;
    ForgetPassword: (obj: ForgetPasswordParams) => Promise<ResultObj>;
    Signout: () => Promise<ResultObj>;
    UserSimulate: (obj: UserSimulateParams) => Promise<ResultObj>;
    Check: () => Promise<ResultObj>;
}

class UserControlApiManager implements UserControlApiInterface {
    private adapter: UserControlDotNet6Adapter | UserControlFrameworkAdapter | null = null;
    private version: keyof typeof API_Version | undefined;

    constructor(version?: keyof typeof API_Version) {
        this.version = version;
    }

    async init(): Promise<UserControlApiManager> {
        let target_version: string | undefined = this.version;
        if (target_version === undefined) {
            // 沒有的話就嘗試取得api_version.json，取得不到就採用localstorage的預設API版本
            let getAPIversionResult: ApiVersionInfo | null = await getControllerApiVersion("UserControl");
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }

        if (target_version === 'dotNet6') this.adapter = new UserControlDotNet6Adapter();
        else if (target_version === 'dotNet6_AM_N') this.adapter = new UserControlDotNet6Adapter();//預設是 dotNet6，有需要才再另寫Adapter
        else if (target_version === 'framework') this.adapter = new UserControlFrameworkAdapter();


        if (this.adapter !== null) {
            this.Signin = this.adapter.Signin;
            this.ForgetPassword = this.adapter.ForgetPassword;
            this.Signout = this.adapter.Signout;
            this.UserSimulate = this.adapter.UserSimulate;
            this.Check = this.adapter.Check;
        }

        return this;
    }

    Signin = async (obj: SigninParams): Promise<ResultObj> => await this.emptyAdapter();
    ForgetPassword = async (obj: ForgetPasswordParams): Promise<ResultObj> => await this.emptyAdapter();
    Signout = async (): Promise<ResultObj> => await this.emptyAdapter();
    UserSimulate = async (obj: UserSimulateParams): Promise<ResultObj> => await this.emptyAdapter();
    Check = async (): Promise<ResultObj> => await this.emptyAdapter();

    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: '遺失 UserControl adapter', data: null });
}

// UserControlDotNet6Adapter 實現
class UserControlDotNet6Adapter implements UserControlApiInterface {
    Signin = async (obj: SigninParams): Promise<ResultObj> => await allFetch('UserControl/Signin', obj);
    ForgetPassword = async (obj: ForgetPasswordParams): Promise<ResultObj> => await allFetch('UserControl/ForgetPassword', obj);
    Signout = async (): Promise<ResultObj> => await allFetch('UserControl/Signout', {});
    UserSimulate = async (obj: UserSimulateParams): Promise<ResultObj> => await allFetch('UserControl/UserSimulate', obj);
    Check = async (): Promise<ResultObj> => await allFetch('UserControl/Check', {});
}

// UserControlFrameworkAdapter 實現
class UserControlFrameworkAdapter implements UserControlApiInterface {
    Signin = async (obj: SigninParams): Promise<ResultObj> => await allFetch('UserControl/Signin', obj);
    ForgetPassword = async (obj: ForgetPasswordParams): Promise<ResultObj> => await allFetch('UserControl/ForgetPassword', obj);
    Signout = async (): Promise<ResultObj> => await allFetch('UserControl/Signout', {});
    UserSimulate = async (obj: UserSimulateParams): Promise<ResultObj> => await allFetch('UserControl/UserSimulate', obj);
    Check = async (): Promise<ResultObj> => await allFetch('UserControl/Check', {});
}




// API呼叫參數類型
export type SigninParams = {
    username: string;
    password: string;
    device_type: string;
    device_code: string;
    check_code: string;
};

export type ForgetPasswordParams = {
    username: string;
    email: string;
};

export type UserSimulateParams = {
    user_id: string;
};
